// HospitalProfileDemoExplorer — homepage "Today's 5 highest-marked-up
// hospitals" slide body (replaced the flat TopHospitalTable, 2026-09-15).
//
// Left rail: a hospital-name search over every ranked hospital, the 5
// highest-markup hospitals (or, on the "Closest to me" tab, the 5 nearest
// ranked hospitals to the visitor's GPS/ZIP location), each with the A–F
// hospital grade badge the rankings, map, and ProcedureDrilldownModal show,
// per-code-family chips that open that same drilldown, and the section tour.
// Right: a scaled-down live iframe of /hospital/<uuid>?embed=1 that the rail
// drives over postMessage — Services (chargemaster) → Quality → Scenarios →
// Price analysis → Financial & ownership → Chargemaster files. After the
// last section it advances to the next of the 5 highest-markup hospitals.
// Auto-cycles when nobody has interacted; pauses on hover; locks to the
// visitor's pick on click. The iframe is mounted once and navigated in-SPA
// (no reloads).
//
// The preview is directly interactive even at preview scale: the visitor can
// scroll it and click through the real tabs (the profile echoes those clicks
// back so the rail follows). There is no hover overlay; the green "traffic
// light" in the chrome strip expands the preview to a viewport takeover, Esc
// (or the Close pill) collapses it.
//
// Message protocol (handled in src/pages/HospitalProfile.tsx):
//   → { type: "demo:hospital", hospitalId, tab }   navigate + switch tab
//   → { type: "demo:tab", tab }                    switch tab only
//   ← { type: "demo:selected", hospitalId, tab }   echo of the active view
//   ← { type: "demo:escape" }                      Esc pressed inside the iframe
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Activity, AlertTriangle, ArrowUpRight, Banknote, Database, FileText, Loader2, MapPin,
  Navigation, Search, Stethoscope, X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { HospitalQualitySnapshot } from "@/components/ProcedureDrilldownModal";
import { getHospitalGradeInfo } from "@/lib/grades";
import { useUserLocation } from "@/hooks/useUserLocation";
import { haversineDistance } from "@/lib/scenarioCoverageStore";
import { loadStaticData } from "@/lib/offlineData";

// Structural subset of HomeBeta's TopHospitalRow — anything with these fields
// can be toured.
export type DemoHospitalRow = {
  hospitalId: string;
  name: string;
  city: string | null;
  state: string | null;
  overallMarkup: number;
  cptMarkup: number | null;
  hcpcsMarkup: number | null;
  drgMarkup: number | null;
  revMarkup: number | null;
  cptMedian: number | null;
  hcpcsMedian: number | null;
  drgMedian: number | null;
  revMedian: number | null;
  quality: HospitalQualitySnapshot;
};

// Theme tokens the explorer needs — HomeBeta's useThemeTokens() output
// satisfies this structurally.
export type DemoTokens = {
  text: string;
  textMuted: string;
  gold: string;
  navy: string;
  red: string;
  cardBg: string;
  cardBorder: string;
  divider: string;
};

type TourStep = {
  tab: "services" | "quality" | "scenarios" | "nearby" | "corruption" | "profile" | "chargemasters";
  // URL suffix shown in the fake browser chrome (matches the real deep-link
  // routes registered in App.tsx; corruption has none, so it deep-links via
  // ?tab=).
  path: string;
  label: string;
  hint: string;
  icon: LucideIcon;
  color: string;
};

// Order = the presentation order the visitor sees. Starts on the chargemaster
// (Services) tab because that's the data the whole site is built on.
export const PROFILE_TOUR_STEPS: TourStep[] = [
  { tab: "services",      path: "",                label: "Services · chargemaster",  hint: "Every price this hospital publishes, searchable by CPT, HCPCS, DRG, revenue, drug, and supply code — with the U.S. median beside each one.", icon: Database,      color: "#3A7BB5" },
  { tab: "quality",       path: "/quality",        label: "Quality",                  hint: "CMS Care Compare: mortality, readmissions, infections, and patient experience next to the state and national median.", icon: Activity, color: "#7C3AED" },
  { tab: "scenarios",     path: "/scenarios",      label: "Scenario pricing",         hint: "What a full episode of care costs here — C-section, hip replacement, appendectomy — priced from this hospital's own file.", icon: Stethoscope, color: "#C5A55A" },
  { tab: "nearby",        path: "/nearby",         label: "City view",                hint: "The same care down the street: this hospital among its neighbours, and which drive is actually worth it.", icon: MapPin, color: "#B22234" },
  { tab: "corruption",    path: "?tab=corruption", label: "Price analysis",           hint: "Markup by code family, outlier rate, and the national rank behind the letter grade.", icon: AlertTriangle, color: "#B45309" },
  { tab: "profile",       path: "/financial",      label: "Financial & ownership",    hint: "Revenue, operating margin, charity care, and executive pay from CMS cost reports and IRS Form 990s.", icon: Banknote, color: "#0F8A5F" },
  { tab: "chargemasters", path: "/chargemasters",  label: "Chargemaster files",       hint: "The hospital's own published price-transparency files, year by year, mirrored to our CDN.", icon: FileText, color: "#7B8AA0" },
];

// Dwell per section. Six steps ≈ 33s per hospital — the whole first-hospital
// pass is what HomeBeta holds its slide rotation for (see onHoldChange).
const STEP_MS = 5500;
// After we post a hospital/tab change, demo:selected echoes for this long are
// treated as our own round-trip rather than a visitor click inside the iframe.
const ECHO_SETTLE_MS = 1500;
// Never hold the parent's slide rotation longer than this, even if the iframe
// never loads (blocked, offline) and the first pass therefore never completes.
const MAX_HOLD_MS = 60_000;
// Preview scale — the iframe renders at 1/SCALE of the container so the
// hospital page lays out at a comfortable desktop width, then shrinks.
const PREVIEW_SCALE = 0.6;
const SEARCH_RESULTS = 6;
const NEAREST_COUNT = 5;

const mono = "ui-monospace, SFMono-Regular, Menlo, monospace";
const sans = "'Inter', system-ui, -apple-system, sans-serif";

// Expanded-mode history. While the takeover is up, every hospital/section
// the visitor lands on becomes a `#preview=/hospital/<uuid><tab path>` entry,
// so Back steps through what they browsed and the URL can be shared; closing
// the takeover drops the hash again. A page loaded with the hash opens
// straight into the takeover on that hospital.
const PREVIEW_HASH_PREFIX = "#preview=";
function previewHashFor(hospitalId: string, step: TourStep): string {
  return `${PREVIEW_HASH_PREFIX}/hospital/${hospitalId}${step.path}`;
}
function parsePreviewHash(hash: string): { hospitalId: string; stepIdx: number } | null {
  if (!hash.startsWith(PREVIEW_HASH_PREFIX)) return null;
  const m = hash.slice(PREVIEW_HASH_PREFIX.length).match(/^\/hospital\/([0-9a-f-]{36})(.*)$/i);
  if (!m) return null;
  const idx = PROFILE_TOUR_STEPS.findIndex((s) => s.path === (m[2] || ""));
  return { hospitalId: m[1], stepIdx: idx >= 0 ? idx : 0 };
}

const CODE_CHIPS: { key: "cpt" | "hcpcs" | "drg" | "rev"; label: string; color: string; drill: string }[] = [
  { key: "cpt",   label: "CPT",   color: "#60a5fa", drill: "cpt" },
  { key: "hcpcs", label: "HCPCS", color: "#4ade80", drill: "hcpcs" },
  { key: "drg",   label: "DRG",   color: "#c084fc", drill: "drg" },
  { key: "rev",   label: "REV",   color: "#fbbf24", drill: "revenue" },
];

// Compact geo pack shared with /near-me: hospital uuid → name/city/state/lat/lng.
type NearMePack = { hospitals: Record<string, { n: string; c: string; s: string; la: number; lo: number }> };

function fmtPct(v: number | null): string {
  if (v == null || !Number.isFinite(v)) return "—";
  const r = Math.round(v);
  const abs = Math.abs(r);
  const txt = abs >= 1000 ? `${(abs / 1000).toFixed(abs >= 10000 ? 0 : 1).replace(/\.0$/, "")}k` : abs.toLocaleString();
  return `${r < 0 ? "−" : "+"}${txt}%`;
}

function fmtMiles(mi: number): string {
  if (mi < 10) return mi.toFixed(1);
  return Math.round(mi).toLocaleString();
}

// Schoolhouse grade — the same letter (and scale) as the rankings table, the
// map, and the report card in ProcedureDrilldownModal: hospital-level
// median-baseline markup through getHospitalGradeInfo.
// getHospitalGradeInfo's declared type intersects the 'N/A' case away, but it
// does return 'N/A' for a null markup — read the letter through string.
function gradeLetter(markup: number | null): { letter: string; color: string; graded: boolean } {
  const gi = getHospitalGradeInfo(markup);
  const letter = String(gi.grade);
  return { letter, color: gi.color, graded: letter !== "N/A" };
}

function GradeBadge({ markup, size = 26, title }: { markup: number | null; size?: number; title?: string }) {
  const g = gradeLetter(markup);
  return (
    <span
      title={title}
      aria-label={g.graded ? `Grade ${g.letter}` : undefined}
      style={{
        width: size, height: size, borderRadius: Math.round(size * 0.23), flexShrink: 0,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        background: g.color, color: "#fff", fontWeight: 900, fontSize: Math.round(size * 0.58),
        lineHeight: 1, boxShadow: "0 1px 2px rgba(0,0,0,0.25)", fontFamily: sans,
      }}
    >
      {g.graded ? g.letter : "–"}
    </span>
  );
}

export function HospitalProfileDemoExplorer({
  T, isDark, rows, allHospitals, onOpenDrilldown, visible, onHoldChange,
}: {
  T: DemoTokens;
  isDark: boolean;
  /** Today's 5 highest-markup hospitals — the auto-tour cycles these. */
  rows: DemoHospitalRow[];
  /** Every ranked hospital — the pool for the name search and "Closest to
   *  me". Omit to hide both. */
  allHospitals?: DemoHospitalRow[];
  onOpenDrilldown: (hospitalId: string, hospitalName: string, procedureType: string, avgMarkup: number, medianMarkup?: number | null, quality?: HospitalQualitySnapshot | null) => void;
  /** False while another hero slide is showing — the explorer stays mounted
   *  (so the iframe never reloads) but stops touring. */
  visible: boolean;
  /** True while the slide rotation should wait for the tour (first full
   *  pass through hospital #1, or the visitor has locked a pick). */
  onHoldChange?: (hold: boolean) => void;
}) {
  const { t } = useTranslation(["homepage"]);
  const tr = (key: string, fallback: string, opts?: Record<string, unknown>) =>
    t(`homepage:homeBeta.heroSlides.nationalIndex.profileTour.${key}`, { defaultValue: fallback, ...(opts || {}) });

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [activeId, setActiveId] = useState<string>(() => rows[0]?.hospitalId ?? "");
  const [stepIdx, setStepIdx] = useState(0);
  const [userLocked, setUserLocked] = useState(false);
  const [paused, setPaused] = useState(false);
  const [firstPassDone, setFirstPassDone] = useState(false);
  const [holdExpired, setHoldExpired] = useState(false);
  const [isExpanded, setIsExpanded] = useState(() =>
    typeof window !== "undefined" && parsePreviewHash(window.location.hash) !== null);
  // Fixed at mount: hospital switches happen in-SPA via postMessage, so the
  // src never changes and the iframe never reloads.
  const [initialSrc] = useState(() => rows[0] ? `/hospital/${rows[0].hospitalId}?embed=1` : "about:blank");
  const lastPostedRef = useRef<{ hospitalId: string; tab: string; at: number }>({
    hospitalId: rows[0]?.hospitalId ?? "", tab: PROFILE_TOUR_STEPS[0].tab, at: 0,
  });

  // ── Search (by name / city over every ranked hospital) ──────────────────
  const [query, setQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [pick, setPick] = useState<DemoHospitalRow | null>(null);
  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2 || !allHospitals?.length) return [] as DemoHospitalRow[];
    const starts: DemoHospitalRow[] = [];
    const contains: DemoHospitalRow[] = [];
    for (const r of allHospitals) {
      const name = r.name.toLowerCase();
      if (name.startsWith(q)) starts.push(r);
      else if (name.includes(q) || (r.city ?? "").toLowerCase().includes(q)) contains.push(r);
      if (starts.length >= SEARCH_RESULTS) break;
    }
    return [...starts, ...contains].slice(0, SEARCH_RESULTS);
  }, [query, allHospitals]);

  // ── Closest to me (GPS / ZIP via the app-global location store) ─────────
  const [listTab, setListTab] = useState<"top" | "near">("top");
  const { location, isLocating, error: locationError, detectLocation, lookupZip } = useUserLocation();
  const [zip, setZip] = useState("");
  const [pack, setPack] = useState<NearMePack | null>(null);
  const [packState, setPackState] = useState<"idle" | "loading" | "ready" | "failed">("idle");
  // Loaded once, the first time the visitor opens "Closest to me". Guarded by
  // a ref rather than packState so flipping to "loading" doesn't re-run (and
  // cancel) this effect mid-download.
  const packRequested = useRef(false);
  useEffect(() => {
    if (listTab !== "near" || packRequested.current) return;
    packRequested.current = true;
    setPackState("loading");
    loadStaticData<NearMePack>("near_me.json")
      .then((p) => { if (p?.hospitals) { setPack(p); setPackState("ready"); } else setPackState("failed"); })
      .catch(() => setPackState("failed"));
  }, [listTab]);
  const nearest = useMemo(() => {
    if (!location || !pack || !allHospitals?.length) return null;
    const scored: { row: DemoHospitalRow; miles: number }[] = [];
    for (const r of allHospitals) {
      const g = pack.hospitals[r.hospitalId];
      if (!g || g.la == null || g.lo == null) continue;
      scored.push({ row: r, miles: haversineDistance(location.lat, location.lng, g.la, g.lo) });
    }
    scored.sort((a, b) => a.miles - b.miles);
    return scored.slice(0, NEAREST_COUNT);
  }, [location, pack, allHospitals]);

  // Every hospital the rail can show — the active one is looked up here.
  const known = useMemo(() => {
    const list: DemoHospitalRow[] = [];
    if (pick) list.push(pick);
    list.push(...rows);
    if (nearest) for (const n of nearest) list.push(n.row);
    return list;
  }, [pick, rows, nearest]);
  const knownRef = useRef(known);
  knownRef.current = known;
  const hospital = known.find((r) => r.hospitalId === activeId) ?? rows[0];
  const step = PROFILE_TOUR_STEPS[stepIdx];

  const activate = useCallback((h: DemoHospitalRow, lock = true) => {
    setActiveId(h.hospitalId);
    setStepIdx(0);
    if (lock) setUserLocked(true);
  }, []);

  // Resolve a hospital id from a preview hash: the rail's lists first, then
  // the full ranked pool (a shared link can name any ranked hospital — it
  // becomes the pinned "Your pick").
  const applyPreviewHash = useCallback((hash: string): boolean => {
    const parsed = parsePreviewHash(hash);
    if (!parsed) return false;
    const h = knownRef.current.find((r) => r.hospitalId === parsed.hospitalId)
      ?? allHospitals?.find((r) => r.hospitalId === parsed.hospitalId);
    if (!h) return false;
    if (!knownRef.current.some((r) => r.hospitalId === h.hospitalId)) setPick(h);
    setActiveId(h.hospitalId);
    setStepIdx(parsed.stepIdx);
    setUserLocked(true);
    setIsExpanded(true);
    return true;
  }, [allHospitals]);

  // Initial deep link (#preview=…) — once the pool is available.
  const deepLinkedRef = useRef(false);
  useEffect(() => {
    if (deepLinkedRef.current || typeof window === "undefined") return;
    if (!parsePreviewHash(window.location.hash)) { deepLinkedRef.current = true; return; }
    if (!allHospitals?.length && !rows.length) return;
    deepLinkedRef.current = true;
    if (!applyPreviewHash(window.location.hash)) setIsExpanded(false);
  }, [allHospitals, rows, applyPreviewHash]);

  // Push a history entry for each view while expanded; clear the hash on close.
  useEffect(() => {
    if (typeof window === "undefined" || !hospital) return;
    if (isExpanded) {
      // A hash we arrived at via Back/Forward or a deep link already equals
      // `next`, so this only pushes for views the visitor navigated to here.
      const next = previewHashFor(hospital.hospitalId, step);
      if (window.location.hash === next) return;
      window.history.pushState({ ahpPreview: true }, "", `${window.location.pathname}${window.location.search}${next}`);
    } else if (window.location.hash.startsWith(PREVIEW_HASH_PREFIX)) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    }
  }, [isExpanded, hospital, step]);

  // Back/Forward: a preview hash re-opens (or re-targets) the takeover; no
  // preview hash closes it.
  useEffect(() => {
    const onPop = () => {
      if (parsePreviewHash(window.location.hash)) {
        if (!applyPreviewHash(window.location.hash)) setIsExpanded(false);
      } else {
        setIsExpanded(false);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [applyPreviewHash]);

  // Landing on the "Closest to me" list with results: show the nearest one.
  const nearestKey = nearest ? nearest.map((n) => n.row.hospitalId).join("|") : "";
  useEffect(() => {
    if (listTab !== "near" || !nearest?.length) return;
    if (nearest.some((n) => n.row.hospitalId === activeId)) return;
    activate(nearest[0].row);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listTab, nearestKey]);

  // Tell the parent whether to hold its slide rotation: through the first
  // full pass (capped at MAX_HOLD_MS of visible time) and while locked.
  useEffect(() => {
    onHoldChange?.((!firstPassDone && !holdExpired) || userLocked);
  }, [firstPassDone, holdExpired, userLocked, onHoldChange]);
  useEffect(() => {
    if (!visible || firstPassDone || holdExpired) return;
    const id = window.setTimeout(() => setHoldExpired(true), MAX_HOLD_MS);
    return () => window.clearTimeout(id);
  }, [visible, firstPassDone, holdExpired]);

  // Esc exits the takeover; lock body scroll while it's up.
  useEffect(() => {
    if (!isExpanded) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") setIsExpanded(false); };
    document.addEventListener("keydown", handler);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.setAttribute("data-explorer-expanded", "true");
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = prevOverflow;
      document.body.removeAttribute("data-explorer-expanded");
    };
  }, [isExpanded]);

  // Drive the iframe. One message shape for both cases — the profile page
  // treats a demo:hospital for the hospital it's already on as a tab switch.
  const postView = useCallback((h: DemoHospitalRow | undefined, s: TourStep) => {
    const win = iframeRef.current?.contentWindow;
    if (!win || !h) return;
    lastPostedRef.current = { hospitalId: h.hospitalId, tab: s.tab, at: Date.now() };
    try {
      win.postMessage({ type: "demo:hospital", hospitalId: h.hospitalId, tab: s.tab }, "*");
    } catch {
      // Iframe navigated away; the next tick re-posts.
    }
  }, []);

  useEffect(() => {
    if (iframeLoaded) postView(hospital, step);
  }, [iframeLoaded, hospital, step, postView]);

  // Echo from the iframe. Anything that isn't our own round-trip is a click
  // inside the preview — mirror it in the rail and lock the tour.
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const iframeWin = iframeRef.current?.contentWindow;
      if (!iframeWin || e.source !== iframeWin) return;
      const msg = e.data;
      if (!msg || typeof msg !== "object") return;
      // Keyboard focus lives inside the iframe once the visitor clicks it,
      // so the parent's own Esc listener never fires — the profile forwards it.
      if (msg.type === "demo:escape") { setIsExpanded(false); return; }
      if (msg.type !== "demo:selected") return;
      const { hospitalId, tab } = msg as { hospitalId?: unknown; tab?: unknown };
      if (typeof hospitalId !== "string" || typeof tab !== "string") return;
      const last = lastPostedRef.current;
      if (last.hospitalId === hospitalId && last.tab === tab) return;
      if (Date.now() - last.at < ECHO_SETTLE_MS) return;
      const h = knownRef.current.find((r) => r.hospitalId === hospitalId);
      const sIdx = PROFILE_TOUR_STEPS.findIndex((s) => s.tab === tab);
      if (!h || sIdx < 0) return;
      lastPostedRef.current = { hospitalId, tab, at: Date.now() };
      setActiveId(hospitalId);
      setStepIdx(sIdx);
      setUserLocked(true);
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // Auto-advance: next section, then the next of the 5 highest-markup
  // hospitals after the last section.
  const cycling = visible && iframeLoaded && !userLocked && !paused && !isExpanded && rows.length > 0;
  useEffect(() => {
    if (!cycling) return;
    const id = window.setInterval(() => {
      setStepIdx((s) => {
        if (s + 1 < PROFILE_TOUR_STEPS.length) return s + 1;
        setActiveId((cur) => {
          const i = rows.findIndex((r) => r.hospitalId === cur);
          return rows[(i + 1) % rows.length].hospitalId;
        });
        setFirstPassDone(true);
        return 0;
      });
    }, STEP_MS);
    return () => window.clearInterval(id);
  }, [cycling, rows]);

  const pickStep = (idx: number) => { setStepIdx(idx); setUserLocked(true); };
  const choosePick = (h: DemoHospitalRow) => {
    setPick(h);
    setQuery("");
    setSearchFocused(false);
    activate(h);
  };
  const clearPick = () => {
    setPick(null);
    if (pick && activeId === pick.hospitalId) activate(rows[0]);
  };
  const submitZip = (e: React.FormEvent) => {
    e.preventDefault();
    if (/^\d{5}$/.test(zip)) void lookupZip(zip);
  };

  if (!hospital) return null;

  const profileHref = `/hospital/${hospital.hospitalId}${step.path}`;
  const chromeUrl = `americanhospitalprices.com/hospital/${hospital.hospitalId.slice(0, 8)}…${step.path}`;
  const StepIcon = step.icon;
  const hasPool = !!allHospitals?.length;
  const gradeTitle = (r: DemoHospitalRow) => {
    const g = gradeLetter(r.quality.overallMedianMarkup);
    return g.graded
      ? tr("gradeTitle", "Overall grade {{grade}} on our A–F scale — this hospital's median markup vs national medians. Same scale as the rankings and map.", { grade: g.letter })
      : tr("gradeNone", "Not enough comparable codes to grade");
  };

  // One hospital row — used by both lists. `meta` is the small line under the
  // markup: "#3 nationally" on the top-5 list, "12 mi" on the nearest list.
  const renderRow = (r: DemoHospitalRow, meta: string | null, extraTop?: React.ReactNode) => {
    const active = r.hospitalId === activeId;
    return (
      <div
        key={r.hospitalId}
        style={{
          borderLeft: `3px solid ${active ? T.gold : "transparent"}`,
          background: active ? `${T.gold}14` : "transparent",
          transition: "background 200ms ease, border-color 200ms ease",
        }}
      >
        {extraTop}
        <button
          type="button"
          onClick={() => activate(r)}
          aria-current={active ? "true" : undefined}
          style={{
            width: "100%",
            textAlign: "left",
            padding: "8px 14px 4px 15px",
            background: "transparent",
            border: 0,
            cursor: "pointer",
            display: "grid",
            gridTemplateColumns: "26px minmax(0, 1fr) auto",
            columnGap: 10,
            alignItems: "start",
            color: T.text,
            fontFamily: sans,
          }}
        >
          <GradeBadge markup={r.quality.overallMedianMarkup} title={gradeTitle(r)} />
          <span style={{ minWidth: 0 }}>
            <span
              style={{
                display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2,
                fontSize: 13, fontWeight: active ? 700 : 600, lineHeight: 1.25, overflow: "hidden",
              }}
              title={r.name}
            >
              {r.name}
            </span>
            <span style={{ display: "block", fontSize: 11, color: T.textMuted, marginTop: 2 }}>
              {[r.city, r.state].filter(Boolean).join(", ")}
            </span>
          </span>
          <span style={{ textAlign: "right", whiteSpace: "nowrap" }}>
            <span style={{ display: "block", fontFamily: mono, fontSize: 13, fontWeight: 800, color: r.overallMarkup < 0 ? "#22c55e" : T.red, marginTop: 1 }}>
              {fmtPct(r.overallMarkup)}
            </span>
            {meta && (
              <span style={{ display: "block", fontFamily: mono, fontSize: 10, color: T.textMuted, marginTop: 2 }}>
                {meta}
              </span>
            )}
          </span>
        </button>
        {/* Per-code-family chips → ProcedureDrilldownModal (same drilldown
            /hospital-rankings opens from its cells). */}
        <div style={{ display: "flex", gap: 4, padding: "0 14px 8px 51px", flexWrap: "wrap" }}>
          {CODE_CHIPS.map((c) => {
            const v = r[`${c.key}Markup` as const];
            const median = r[`${c.key}Median` as const];
            const hasValue = v != null && Number.isFinite(v);
            return (
              <button
                key={c.key}
                type="button"
                disabled={!hasValue}
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasValue) onOpenDrilldown(r.hospitalId, r.name, c.drill, v as number, median, r.quality);
                }}
                title={hasValue
                  ? tr("chipTitle", "Open the {{family}} procedure-by-procedure breakdown for {{hospital}}", { family: c.label, hospital: r.name })
                  : tr("chipNoData", "No {{family}} benchmark for this hospital", { family: c.label })}
                style={{
                  fontFamily: mono, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.02em",
                  padding: "2px 6px", borderRadius: 999,
                  border: `1px solid ${c.color}${hasValue ? "66" : "22"}`,
                  background: `${c.color}${hasValue ? "1a" : "08"}`,
                  color: hasValue ? c.color : T.textMuted,
                  cursor: hasValue ? "pointer" : "default",
                  opacity: hasValue ? 1 : 0.55,
                  lineHeight: 1.3,
                }}
              >
                {c.label} {fmtPct(v)}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const rankMeta = (r: DemoHospitalRow) =>
    r.quality.overallRank != null ? tr("rankShort", "#{{rank}} nationally", { rank: r.quality.overallRank.toLocaleString() }) : null;

  const inputStyle: React.CSSProperties = {
    width: "100%", fontFamily: sans, fontSize: 13, color: T.text,
    background: isDark ? "rgba(250,249,246,0.05)" : "rgba(27,27,27,0.035)",
    border: `1px solid ${T.cardBorder}`, borderRadius: 6, outline: "none",
  };
  const segStyle = (on: boolean): React.CSSProperties => ({
    flex: 1, padding: "6px 8px", fontFamily: sans, fontSize: 11.5, fontWeight: 700, letterSpacing: "0.02em",
    borderRadius: 5, border: 0, cursor: "pointer",
    background: on ? T.gold : "transparent", color: on ? "#1B1B1B" : T.textMuted,
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5,
  });

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="grid grid-cols-1 lg:grid-cols-[380px_1fr] items-stretch"
      style={{
        background: T.cardBg,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 10,
        overflow: "hidden",
        boxShadow: `0 24px 64px ${isDark ? "rgba(0,0,0,0.5)" : "rgba(0,40,104,0.08)"}`,
      }}
    >
      <style>{`
        @keyframes hp-demo-progress { from { width: 0% } to { width: 100% } }
      `}</style>

      {/* ── Left rail ─────────────────────────────────────────────────── */}
      <div style={{ borderRight: `1px solid ${T.cardBorder}`, padding: "18px 0 14px", display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div style={{ padding: "0 18px 4px", fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: T.gold }}>
          {tr("hospitalsHeader", "Today's 5 · highest markups")}
        </div>
        <div style={{ padding: "0 18px 10px", fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>
          {userLocked
            ? tr("locked", "Click a hospital or section to switch the live preview →")
            : tr("cycling", "Auto-touring each profile · click to lock")}
        </div>

        {/* Search any ranked hospital */}
        {hasPool && (
          <div style={{ padding: "0 18px 10px", position: "relative" }}>
            <div style={{ position: "relative" }}>
              <Search className="w-3.5 h-3.5" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: T.textMuted, pointerEvents: "none" }} />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => window.setTimeout(() => setSearchFocused(false), 150)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") { setQuery(""); (e.target as HTMLInputElement).blur(); }
                  if (e.key === "Enter" && searchResults[0]) choosePick(searchResults[0]);
                }}
                placeholder={tr("searchPlaceholder", "Search any ranked hospital…")}
                aria-label={tr("searchPlaceholder", "Search any ranked hospital…")}
                autoComplete="off"
                style={{ ...inputStyle, padding: "8px 10px 8px 30px" }}
              />
            </div>
            {searchFocused && query.trim().length >= 2 && (
              <div
                role="listbox"
                style={{
                  position: "absolute", left: 18, right: 18, top: "100%", zIndex: 20, marginTop: 4,
                  background: T.cardBg, border: `1px solid ${T.cardBorder}`, borderRadius: 8,
                  boxShadow: `0 16px 40px ${isDark ? "rgba(0,0,0,0.55)" : "rgba(0,40,104,0.16)"}`,
                  overflow: "hidden",
                }}
              >
                {searchResults.length === 0 ? (
                  <div style={{ padding: "10px 12px", fontSize: 12, color: T.textMuted }}>
                    {tr("searchNoResults", "No ranked hospital matches “{{q}}”", { q: query.trim() })}
                  </div>
                ) : searchResults.map((r) => (
                  <button
                    key={r.hospitalId}
                    type="button"
                    role="option"
                    aria-selected={r.hospitalId === activeId}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choosePick(r)}
                    style={{
                      width: "100%", textAlign: "left", border: 0, cursor: "pointer", background: "transparent",
                      padding: "8px 12px", display: "grid", gridTemplateColumns: "22px minmax(0,1fr) auto",
                      columnGap: 10, alignItems: "center", color: T.text, fontFamily: sans,
                      borderTop: `1px solid ${T.divider}`,
                    }}
                    className="hover:bg-black/5 dark:hover:bg-white/5"
                  >
                    <GradeBadge markup={r.quality.overallMedianMarkup} size={22} title={gradeTitle(r)} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
                      <span style={{ display: "block", fontSize: 10.5, color: T.textMuted }}>{[r.city, r.state].filter(Boolean).join(", ")}</span>
                    </span>
                    <span style={{ fontFamily: mono, fontSize: 12, fontWeight: 800, color: r.overallMarkup < 0 ? "#22c55e" : T.red }}>{fmtPct(r.overallMarkup)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* The visitor's searched hospital, pinned above the lists */}
        {pick && renderRow(pick, rankMeta(pick), (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 14px 0 15px" }}>
            <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: T.gold }}>
              {tr("yourPick", "Your pick")}
            </span>
            <button
              type="button"
              onClick={clearPick}
              aria-label={tr("clearPick", "Clear your pick")}
              title={tr("clearPick", "Clear your pick")}
              style={{ border: 0, background: "transparent", color: T.textMuted, cursor: "pointer", padding: 2, display: "inline-flex" }}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}

        {/* List switch: today's 5 worst ↔ 5 closest to the visitor */}
        {hasPool && (
          <div style={{ margin: "2px 18px 6px", padding: 3, display: "flex", gap: 3, background: isDark ? "rgba(250,249,246,0.05)" : "rgba(27,27,27,0.05)", borderRadius: 7 }}>
            <button type="button" onClick={() => setListTab("top")} aria-pressed={listTab === "top"} style={segStyle(listTab === "top")}>
              {tr("tabTop", "Highest markups")}
            </button>
            <button type="button" onClick={() => { setListTab("near"); setUserLocked(true); }} aria-pressed={listTab === "near"} style={segStyle(listTab === "near")}>
              <MapPin className="w-3 h-3" /> {tr("tabNear", "Closest to me")}
            </button>
          </div>
        )}

        {listTab === "top" && rows.map((r) => renderRow(r, rankMeta(r)))}

        {listTab === "near" && (
          <div>
            {!location ? (
              <div style={{ padding: "6px 18px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>
                  {tr("nearIntro", "The 5 nearest ranked hospitals, graded A–F on their own published prices.")}
                </div>
                <button
                  type="button"
                  onClick={() => void detectLocation()}
                  disabled={isLocating}
                  style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
                    background: T.navy, color: "#FAF9F6", border: 0, borderRadius: 6, padding: "9px 12px",
                    fontFamily: sans, fontSize: 13, fontWeight: 700, cursor: isLocating ? "wait" : "pointer",
                  }}
                >
                  {isLocating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
                  {tr("useMyLocation", "Use my location")}
                </button>
                <form onSubmit={submitZip} style={{ display: "flex", gap: 6 }}>
                  <input
                    inputMode="numeric"
                    maxLength={5}
                    value={zip}
                    onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
                    placeholder={tr("zipPlaceholder", "or enter a ZIP code")}
                    aria-label={tr("zipPlaceholder", "or enter a ZIP code")}
                    style={{ ...inputStyle, padding: "8px 10px", flex: 1 }}
                  />
                  <button
                    type="submit"
                    disabled={!/^\d{5}$/.test(zip) || isLocating}
                    style={{
                      background: T.gold, color: "#1B1B1B", border: 0, borderRadius: 6, padding: "0 14px",
                      fontFamily: sans, fontSize: 12.5, fontWeight: 800, cursor: "pointer",
                      opacity: /^\d{5}$/.test(zip) ? 1 : 0.5,
                    }}
                  >
                    {tr("zipGo", "Go")}
                  </button>
                </form>
                {locationError && (
                  <div style={{ fontSize: 11.5, color: T.red, lineHeight: 1.45 }}>{locationError}</div>
                )}
              </div>
            ) : (
              <>
                <div style={{ padding: "2px 18px 6px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ fontSize: 11, color: T.textMuted, display: "inline-flex", alignItems: "center", gap: 5, minWidth: 0 }}>
                    <MapPin className="w-3 h-3" style={{ color: T.red, flexShrink: 0 }} />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {tr("nearHeader", "Closest ranked hospitals to {{label}}", { label: location.label })}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => void detectLocation()}
                    title={tr("useMyLocation", "Use my location")}
                    style={{ border: 0, background: "transparent", color: T.gold, cursor: "pointer", fontFamily: sans, fontSize: 11, fontWeight: 700, padding: 0, flexShrink: 0 }}
                  >
                    {isLocating ? <Loader2 className="w-3 h-3 animate-spin" /> : tr("relocate", "Use GPS")}
                  </button>
                </div>
                {packState === "failed" ? (
                  <div style={{ padding: "8px 18px 12px", fontSize: 12, color: T.red }}>
                    {tr("nearFailed", "Couldn't load hospital locations right now.")}
                  </div>
                ) : !nearest ? (
                  <div style={{ padding: "8px 18px 12px", fontSize: 12, color: T.textMuted, display: "flex", alignItems: "center", gap: 8 }}>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> {tr("nearLoading", "Finding the 5 closest ranked hospitals…")}
                  </div>
                ) : nearest.length === 0 ? (
                  <div style={{ padding: "8px 18px 12px", fontSize: 12, color: T.textMuted }}>
                    {tr("nearEmpty", "No ranked hospitals with a known location near {{label}}.", { label: location.label })}
                  </div>
                ) : nearest.map((n) => renderRow(n.row, tr("miles", "{{miles}} mi", { miles: fmtMiles(n.miles) })))}
                <form onSubmit={submitZip} style={{ display: "flex", gap: 6, padding: "4px 18px 8px" }}>
                  <input
                    inputMode="numeric"
                    maxLength={5}
                    value={zip}
                    onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
                    placeholder={tr("zipChange", "Try another ZIP")}
                    aria-label={tr("zipChange", "Try another ZIP")}
                    style={{ ...inputStyle, padding: "6px 10px", flex: 1, fontSize: 12 }}
                  />
                  <button
                    type="submit"
                    disabled={!/^\d{5}$/.test(zip) || isLocating}
                    style={{
                      background: "transparent", color: T.gold, border: `1px solid ${T.cardBorder}`, borderRadius: 6, padding: "0 12px",
                      fontFamily: sans, fontSize: 12, fontWeight: 800, cursor: "pointer", opacity: /^\d{5}$/.test(zip) ? 1 : 0.5,
                    }}
                  >
                    {tr("zipGo", "Go")}
                  </button>
                </form>
              </>
            )}
          </div>
        )}

        {/* ── Section tour ─────────────────────────────────────────────── */}
        <div style={{ padding: "14px 18px 4px", fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: T.textMuted, borderTop: `1px solid ${T.divider}`, marginTop: 6 }}>
          {tr("sectionsHeader", "Profile tour · {{count}} sections", { count: PROFILE_TOUR_STEPS.length })}
        </div>
        {PROFILE_TOUR_STEPS.map((s, idx) => {
          const active = idx === stepIdx;
          const Icon = s.icon;
          return (
            <button
              key={s.tab}
              type="button"
              onClick={() => pickStep(idx)}
              aria-current={active ? "true" : undefined}
              style={{
                position: "relative",
                width: "100%",
                textAlign: "left",
                padding: "8px 18px 9px 21px",
                background: active ? `${s.color}14` : "transparent",
                borderLeft: `3px solid ${active ? s.color : "transparent"}`,
                borderTop: 0, borderRight: 0, borderBottom: 0,
                cursor: "pointer",
                color: T.text,
                fontFamily: sans,
                transition: "background 200ms ease, border-color 200ms ease",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: active ? 700 : 500 }}>
                <Icon className="w-3.5 h-3.5" style={{ color: active ? s.color : T.textMuted, flexShrink: 0 }} />
                {tr(`steps.${s.tab}.label`, s.label)}
              </span>
              {active && (
                <span style={{ display: "block", fontSize: 11.5, color: T.textMuted, lineHeight: 1.45, marginTop: 4, paddingLeft: 22 }}>
                  {tr(`steps.${s.tab}.hint`, s.hint)}
                </span>
              )}
              {/* Dwell progress — restarts on every hospital/step change,
                  freezes while hovered or locked. */}
              {active && (
                <span
                  aria-hidden
                  key={`${hospital.hospitalId}-${idx}`}
                  style={{
                    position: "absolute", left: 0, bottom: 0, height: 2,
                    background: s.color, opacity: 0.8,
                    animation: `hp-demo-progress ${STEP_MS}ms linear both`,
                    animationPlayState: cycling ? "running" : "paused",
                    width: cycling ? undefined : (userLocked ? "100%" : "0%"),
                  }}
                />
              )}
            </button>
          );
        })}

        <div style={{ padding: "14px 18px 0", marginTop: "auto" }}>
          <Link
            to={profileHref}
            className="inline-flex items-center gap-1.5"
            style={{ color: T.gold, fontSize: 13, fontWeight: 600, textDecoration: "none" }}
          >
            {tr("openFull", "Open this hospital's full profile")} <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* ── Right: live scaled preview ─────────────────────────────────── */}
      <div style={{ position: "relative", background: "#0a1628", display: "flex", flexDirection: "column", minWidth: 0 }}>
        <div
          className="flex items-center gap-3 px-4 py-2"
          style={{
            borderBottom: `1px solid ${T.cardBorder}`,
            fontSize: 12,
            color: T.textMuted,
            background: isDark ? "rgba(255,255,255,0.02)" : "rgba(27,27,27,0.025)",
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          <div className="flex items-center gap-1.5" style={{ flexShrink: 0 }}>
            <span aria-hidden style={{ width: 10, height: 10, borderRadius: "50%", background: "#ff5f57" }} />
            <span aria-hidden style={{ width: 10, height: 10, borderRadius: "50%", background: "#febc2e" }} />
            {/* The green light is the expand control — same affordance as a
                macOS window's zoom button. */}
            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              title={isExpanded ? tr("close", "Close") : tr("expand", "Expand to fill tab")}
              aria-label={isExpanded ? tr("closeAria", "Close fullscreen preview") : tr("expand", "Expand to fill tab")}
              aria-pressed={isExpanded}
              className="transition-transform hover:scale-125"
              style={{
                width: 10, height: 10, borderRadius: "50%", background: "#28c840",
                border: 0, padding: 0, cursor: "pointer",
                boxShadow: "0 0 0 2px rgba(40,200,64,0.18)",
              }}
            />
          </div>
          {/* Centered: what this frame is + a way out of it that keeps the
              visitor's place (same hospital, same section, own tab). */}
          <div className="flex-1 flex items-center justify-center gap-3" style={{ minWidth: 0 }}>
            <span style={{ fontSize: 11.5, color: T.textMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={chromeUrl}>
              {tr("interactiveNote", "Interactive preview — scroll and click through the real page")}
            </span>
            <a
              href={profileHref}
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-1 transition-transform hover:translate-x-0.5"
              title={chromeUrl}
              style={{
                fontFamily: sans, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", textDecoration: "none",
                color: T.gold, border: `1px solid ${T.gold}66`, borderRadius: 999, padding: "3px 10px",
                background: `${T.gold}14`, flexShrink: 0,
              }}
            >
              {tr("openOwnPage", "Open on its own page")} <ArrowUpRight className="w-3 h-3" />
            </a>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1.5" style={{ fontSize: 11, color: step.color, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 }}>
            <StepIcon className="w-3 h-3" />
            {tr("liveBadge", "Live ·")} {tr(`steps.${step.tab}.label`, step.label)}
          </span>
        </div>

        <div
          style={
            isExpanded
              ? { position: "fixed", inset: 0, zIndex: 9998, background: "#0a1628", overflow: "hidden" }
              : { position: "relative", flex: "1 1 auto", minHeight: 600, overflow: "hidden", background: "#0a1628" }
          }
        >
          <iframe
            ref={iframeRef}
            src={initialSrc}
            title={tr("iframeTitle", "Live hospital profile — guided preview")}
            loading="lazy"
            onLoad={() => setIframeLoaded(true)}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: isExpanded ? "100%" : `${(100 / PREVIEW_SCALE).toFixed(4)}%`,
              height: isExpanded ? "100%" : `${(100 / PREVIEW_SCALE).toFixed(4)}%`,
              border: 0,
              transform: isExpanded ? "none" : `scale(${PREVIEW_SCALE})`,
              transformOrigin: "top left",
              // Interactive at preview scale too — the browser hit-tests
              // through the CSS transform, so scrolling and tab clicks land.
              pointerEvents: "auto",
              background: "#0a1628",
            }}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            referrerPolicy="same-origin"
          />
          {!iframeLoaded && (
            <div
              aria-hidden
              style={{
                position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
                color: "rgba(250,249,246,0.6)", fontFamily: sans, fontSize: 13, pointerEvents: "none",
              }}
            >
              {tr("loading", "Loading live profile…")}
            </div>
          )}
          {/* Close button — only when expanded. Top-right, sits over the
              iframe at high z-index so the visitor can dismiss the takeover
              without hunting for the Esc key. */}
          {isExpanded && (
            <a
              href={profileHref}
              target="_blank"
              rel="noopener"
              title={chromeUrl}
              style={{
                position: "absolute",
                top: 16,
                right: 132,
                zIndex: 10000,
                background: "rgba(10,22,40,0.85)",
                color: "#FAF9F6",
                border: "1px solid rgba(250,249,246,0.2)",
                borderRadius: 999,
                padding: "8px 14px",
                fontFamily: sans,
                fontSize: 13,
                fontWeight: 600,
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
              }}
            >
              {tr("openOwnPage", "Open on its own page")} <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          )}
          {isExpanded && (
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              aria-label={tr("closeAria", "Close fullscreen preview")}
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 10000,
                background: "rgba(10,22,40,0.85)",
                color: "#FAF9F6",
                border: "1px solid rgba(250,249,246,0.2)",
                borderRadius: 999,
                padding: "8px 14px",
                fontFamily: sans,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
              }}
            >
              {tr("close", "Close")} <span style={{ fontFamily: mono, fontSize: 11, opacity: 0.7 }}>(Esc)</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
